// useMetricsRbac — porta ÚNICA do RBAC do módulo no front (MET-27).
//
// Espelha o `MetricsScope` do back. Nenhuma view pergunta `role === 'GESTOR'`
// direto: um papel novo de baixo privilégio cairia no branch errado (foi o que
// aconteceu com o ATENDENTE quando ele entrou na hierarquia). Quem responde é o
// predicado nomeado.
//
// O clamp de valor (qual vendedor, qual loja) NÃO está aqui — está em
// `selectMetricsQueryArgs`, para que nenhum componente possa esquecê-lo. Este hook
// responde só o que a UI precisa MOSTRAR ou ESCONDER.
import { useMemo } from 'react';
import { useAppSelector } from '@/app/store/hooks';
import { selectCurrentUser } from '@/features/auth';
import { isManager, type Role } from '@/config/constants';

export interface MetricsRbac {
  role: Role | null;
  userId: string | null;
  /** Lojas do escopo do usuário. Vazio = escopo desconhecido nesta sessão. */
  storeIds: string[];
  /**
   * MET-22 (qualidade/tier do número) é só do GESTOR — no back são dois endpoints
   * distintos (`/canal/saude` × `/canal/restricao`), não um payload filtrado, para
   * que um erro de serialização não consiga vazar `qualityRating`.
   */
  canSeeNumberHealth: boolean;
  /** Lê a aba de configuração (SUPERVISOR pra cima). */
  canSeeConfig: boolean;
  /** Escreve configuração (só GESTOR; toda escrita é auditada no back). */
  canConfigure: boolean;
  /** VENDEDOR/ATENDENTE veem apenas os próprios números. */
  selfOnly: boolean;
  /** Pode escolher outro vendedor no filtro global. */
  canFilterByVendor: boolean;
  /** Pode escolher outra loja no filtro global. */
  canFilterByStore: boolean;
}

export function useMetricsRbac(): MetricsRbac {
  const user = useAppSelector(selectCurrentUser);

  return useMemo(() => {
    const role = user?.role ?? null;
    const manager = isManager(role);
    const selfOnly = role === 'VENDEDOR' || role === 'ATENDENTE';
    return {
      role,
      userId: user?.id ?? null,
      storeIds: user?.storeIds ?? [],
      canSeeNumberHealth: role === 'GESTOR',
      canSeeConfig: manager,
      canConfigure: role === 'GESTOR',
      selfOnly,
      canFilterByVendor: !selfOnly,
      canFilterByStore: manager,
    };
  }, [user]);
}
